
import { motion } from 'framer-motion'
import { Calendar, Clock, MapPin, Users, DollarSign, Share2 } from 'lucide-react'
import { Body } from '@meiuca/ui-components'
import { getNivelColors, getVagasColors, buildColorClasses, getParticipationStatusInfo } from '@/utils/gameUtils'
import { Game, getGameLevelLabel, getGameModalityLabel, formatGameDate, formatGameTime, ChargeType, ParticipationStatus } from '@/types/game'
import { GameActions } from './GameActions'

interface GameItemProps {
  game: Game
  onShare?: (game: Game) => void
  onGameClick?: (gameId: string) => void
  onJoinGame?: (gameId: string) => void
  onConfirmParticipation?: (gameId: string) => void
  onDeclineInvitation?: (gameId: string) => void
  onCancelParticipation?: (gameId: string) => void
}

export function GameItem({ 
  game, 
  onShare, 
  onGameClick, 
  onJoinGame,
  onConfirmParticipation, 
  onDeclineInvitation, 
  onCancelParticipation 
}: GameItemProps) {
  const formatDateForCard = (date: Date) => {
    if (!date || isNaN(date.getTime())) return 'Invalid Date'
    return date.toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    })
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const getPaymentInfo = () => {
    if (game.paymentMode === ChargeType.DIVIDED) {
      const totalPrice = formatPrice(game.price)
      const pricePerPerson = game.totalSlots > 0 ? formatPrice(game.price / game.totalSlots) : formatPrice(0)
      return {
        mainPrice: totalPrice,
        description: `Total dividido entre ${game.totalSlots} pessoas`,
        perPerson: pricePerPerson,
        icon: '🧮',
        type: 'Dividido'
      }
    } else {
      return {
        mainPrice: formatPrice(game.price),
        description: 'Valor fixo por pessoa',
        perPerson: null,
        icon: '🎯',
        type: 'Fixo'
      }
    }
  }

  const getVagasColor = () => {
    const percentOccupied = (game.occupiedSlots / game.totalSlots) * 100
    if (percentOccupied >= 90) return 'text-red-600'
    if (percentOccupied >= 70) return 'text-orange-600'
    return 'text-green-600'
  }

  const vagasRestantes = game.totalSlots - game.occupiedSlots

  // Get semantic colors from design system
  const nivelColors = getNivelColors(game.level)
  const vagasColors = getVagasColors(game.occupiedSlots, game.totalSlots)

  const statusInfo = getParticipationStatusInfo(game.userStatus)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      onClick={() => onGameClick?.(game.id)}
      className="bg-background-white rounded-xl shadow-sm p-6 border border-border-primary hover:shadow-md transition-all duration-200 cursor-pointer"
    >
      <div className="flex flex-col space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-4">
            <div className="mb-2">
              <h3 className="font-semibold text-text-primary text-lg mb-2">{game.name}</h3>
              <div className="flex flex-wrap items-center gap-2">
                {statusInfo && (
                  <span className={`px-2 py-1 text-xs font-medium rounded-lg border ${statusInfo.className}`}>
                    {statusInfo.label}
                  </span>
                )}
                <span className={`px-2 py-1 text-sm font-medium rounded-lg ${buildColorClasses(vagasColors)}`}>
                  {vagasRestantes} {vagasRestantes === 1 ? 'vaga' : 'vagas'}
                </span>
                <span className={`px-2 py-1 text-xs rounded-full border ${buildColorClasses(nivelColors)}`}>
                  {getGameLevelLabel(game.level)}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-1 text-text-secondary">
              <span className="text-sm">{getGameModalityLabel(game.modality)}</span>
            </div>
          </div>
          
          {onShare && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onShare(game)
              }}
              className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
              title="Compartilhar jogo"
            >
              <Share2 className="w-5 h-5 text-text-secondary" />
            </button>
          )}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-3">
            {/* Data e Hora */}
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-brand-secondary" />
              <Body className="text-sm">
                {formatDateForCard(game.startDate)}
              </Body>
              <Clock className="w-4 h-4 text-brand-secondary ml-2" />
              <Body className="text-sm">
                {formatGameTime(game.startDate)}
              </Body>
            </div>

            {/* Local */}
            <div className="flex items-start space-x-2">
              <MapPin className="w-4 h-4 text-brand-secondary mt-0.5 flex-shrink-0" />
              <div>
                <Body className="text-sm font-medium">{game.location}</Body>
                <Body className="text-xs text-text-secondary">{game.address}</Body>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            {/* Total de jogadores */}
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-brand-secondary" />
              <Body className="text-sm text-text-secondary">
                {game.occupiedSlots}/{game.totalSlots} jogadores
              </Body>
            </div>

            {/* Valor e Forma de Cobrança */}
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-4 h-4 text-brand-secondary" />
                <div className="flex items-center space-x-2">
                  <Body className="text-sm font-medium">
                    {getPaymentInfo().mainPrice}
                  </Body>
                  <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium">
                    {getPaymentInfo().icon} {getPaymentInfo().type}
                  </span>
                </div>
              </div>
              <div className="ml-6 text-xs text-text-secondary">
                {getPaymentInfo().description}
                {getPaymentInfo().perPerson && (
                  <span className="ml-2 text-brand-primary font-medium">
                    → {getPaymentInfo().perPerson}/pessoa
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Game Actions */}
        <div onClick={(e) => e.stopPropagation()}>
          <GameActions 
            game={game}
            onJoinGame={onJoinGame}
            onConfirmParticipation={onConfirmParticipation}
            onDeclineInvitation={onDeclineInvitation}
            onCancelParticipation={onCancelParticipation}
          />
        </div>
      </div>
    </motion.div>
  )
}